import { buildApiUrl } from "./Static";
import { authHeaders } from "./AuthState";
import {
  formatBrandMaterialName,
  formatBrandMeta,
  getRowCustomerBrand,
} from "./brandData";
import { formatCharacterLabels } from "./MaterialDetails";
import materialSupportNotes from "../data/materialSupportNotes.json";

const PHOTO_W = 1024;
const PHOTO_H = 768;
const RAIL_W = 400;
const CARD_W = PHOTO_W + RAIL_W;
const CARD_H = PHOTO_H;
const RAIL_PAD = 22;
const JPEG_QUALITY = 0.8;

const COLORS = {
  rail: "#171e1e",
  railTop: "#1e2626",
  vendor: "#00ffed",
  title: "#f4fffd",
  body: "#c7d2d1",
  label: "#9bb0ae",
  value: "#eef8f7",
  price: "#b9d8d5",
  rule: "#ffffff22",
  accent: "#00ffed",
  footer: "#141a1a",
};

export function wrapText(ctx, text, maxWidth, maxLines = 4) {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) {
    lines.push(current);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const clipped = lines.slice(0, maxLines);
  const last = clipped[maxLines - 1];
  clipped[maxLines - 1] =
    last.length > 3 ? `${last.replace(/\s+\S*$/, "")}…` : `${last}…`;
  return clipped;
}

export function cardSiteBase() {
  if (
    typeof window !== "undefined" &&
    /matlib\.net$/i.test(window.location.hostname)
  ) {
    return "https://matlib.net";
  }
  return "https://matlib.co";
}

export function cardFileName(vendor, slug, audience = "", format = "card") {
  if (format === "named") {
    return `${vendor}_${slug}.jpg`;
  }

  const brand = String(audience || "")
    .trim()
    .toLowerCase();
  const suffix = brand ? `_${brand}` : "";
  return `${vendor}_${slug}${suffix}_card.jpg`;
}

const CARD_AUDIENCE_BRANDS = ["nike", "adidas"];

export function getCardAudienceOptions(brandData) {
  const present = new Set(
    (Array.isArray(brandData) ? brandData : [])
      .map((row) => getRowCustomerBrand(row))
      .filter((brand) => CARD_AUDIENCE_BRANDS.includes(brand)),
  );

  return [
    { id: "", label: "No brand info" },
    ...CARD_AUDIENCE_BRANDS.filter((brand) => present.has(brand)).map(
      (brand) => ({
        id: brand,
        label: brand.charAt(0).toUpperCase() + brand.slice(1),
      }),
    ),
  ];
}

export function vendorCardNeedsAudience(brandData) {
  return getCardAudienceOptions(brandData).length > 1;
}

/** "" omits brand lines. A brand id filters to that brand. Null keeps the session filter. */
export function pdfBrandQuery(audience, sessionBrand) {
  if (audience === "") {
    return { omit: true, brand: "" };
  }

  const brand = String(audience || sessionBrand || "")
    .trim()
    .toLowerCase();
  return { omit: false, brand };
}

export function brandDataForCard(brandData, audience) {
  const needle = String(audience || "")
    .trim()
    .toLowerCase();
  if (!needle) {
    return [];
  }

  return (Array.isArray(brandData) ? brandData : []).filter(
    (row) => getRowCustomerBrand(row) === needle,
  );
}

export function getCardSupportNotes(type, vendor) {
  const normalizedType = String(type || "").toLowerCase();
  const normalizedVendor = String(vendor || "").toLowerCase();
  const source =
    normalizedType === "split" && normalizedVendor === "tectuff"
      ? materialSupportNotes.split_tectuff
      : normalizedType === "split"
        ? materialSupportNotes.split
        : materialSupportNotes.default;

  return {
    title: source?.title || materialSupportNotes.default.title,
    items: Array.isArray(source?.info)
      ? source.info
      : materialSupportNotes.default.info,
  };
}

export function getCardInfoSections(material, brandData) {
  const brands = Array.isArray(brandData) ? brandData.slice(0, 2) : [];
  return {
    brands,
    notes: getCardSupportNotes(material?.type, material?.vendor),
  };
}

function fetchImageData(imageUrl) {
  return fetch(buildApiUrl("/upload/download"), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ image: imageUrl }),
  }).then(async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.message || `Download failed (${response.status})`);
    }

    const imageData = typeof payload === "string" ? payload : payload?.data;
    if (!imageData || typeof imageData !== "string") {
      throw new Error("Download response did not include image data");
    }

    return imageData;
  });
}

function drawMetaRow(ctx, x, y, width, items) {
  const col = width / items.length;
  items.forEach((item, index) => {
    const left = x + col * index;
    ctx.fillStyle = COLORS.label;
    ctx.font = "bold 9px Montserrat, Helvetica, sans-serif";
    ctx.fillText(item.label.toUpperCase(), left, y);
    ctx.fillStyle = COLORS.value;
    ctx.font = "bold 12px Montserrat, Helvetica, sans-serif";
    ctx.fillText(String(item.value || "--").toUpperCase(), left, y + 16);
  });
}

function drawRail(ctx, material, brandData) {
  const x = PHOTO_W;
  const width = RAIL_W;
  const footerTop = CARD_H - 48;
  const gradient = ctx.createLinearGradient(x, 0, x, CARD_H);
  gradient.addColorStop(0, COLORS.railTop);
  gradient.addColorStop(1, COLORS.rail);
  ctx.fillStyle = gradient;
  ctx.fillRect(x, 0, width, CARD_H);

  const left = x + RAIL_PAD;
  const textWidth = width - RAIL_PAD * 2;
  let y = 36;

  ctx.fillStyle = COLORS.vendor;
  ctx.font = "bold 11px Montserrat, Helvetica, sans-serif";
  ctx.fillText(String(material.vendor || "").toUpperCase(), left, y);
  y += 28;

  ctx.fillStyle = COLORS.title;
  ctx.font = "bold 22px Montserrat, Helvetica, sans-serif";
  wrapText(ctx, String(material.title || "").toUpperCase(), textWidth, 3).forEach(
    (line) => {
      ctx.fillText(line, left, y);
      y += 26;
    },
  );
  y += 6;

  ctx.fillStyle = COLORS.body;
  ctx.font = "13px Montserrat, Helvetica, sans-serif";
  wrapText(
    ctx,
    material.description || "Material details for reference.",
    textWidth,
    3,
  ).forEach((line) => {
    ctx.fillText(line, left, y);
    y += 18;
  });
  y += 16;

  ctx.fillStyle = COLORS.rule;
  ctx.fillRect(left, y, textWidth, 1);
  y += 22;

  drawMetaRow(ctx, left, y, textWidth, [
    { label: "Substrate", value: material.substrate },
    { label: "Type", value: material.type },
    { label: "Group", value: material.subtype },
  ]);
  y += 36;

  ctx.fillStyle = COLORS.rule;
  ctx.fillRect(left, y, textWidth, 1);
  y += 32;

  const col = textWidth / 2;
  const priceCenter = left + col / 2;
  const statusCenter = left + col + col / 2;
  const priceLabel = `$${Number(material.price || 0).toFixed(2)}`;
  const priceY = y + 24;
  ctx.font = "bold 28px Montserrat, Helvetica, sans-serif";
  const priceWidth = ctx.measureText(priceLabel).width;
  ctx.font = "bold 10px Montserrat, Helvetica, sans-serif";
  const uomWidth = ctx.measureText("/ SQ.FT.").width;
  const priceGroupLeft = priceCenter - (priceWidth + 8 + uomWidth) / 2;
  ctx.fillStyle = COLORS.price;
  ctx.font = "bold 28px Montserrat, Helvetica, sans-serif";
  ctx.fillText(priceLabel, priceGroupLeft, priceY);
  ctx.fillStyle = COLORS.label;
  ctx.font = "bold 10px Montserrat, Helvetica, sans-serif";
  ctx.fillText("/ SQ.FT.", priceGroupLeft + priceWidth + 8, priceY - 1);
  ctx.textAlign = "center";
  ctx.fillText("STATUS", statusCenter, y + 10);
  ctx.fillStyle = COLORS.value;
  ctx.font = "bold 12px Montserrat, Helvetica, sans-serif";
  ctx.fillText(String(material.status || "--").toUpperCase(), statusCenter, priceY);
  y = priceY + 18;
  ctx.fillStyle = COLORS.label;
  ctx.font = "7px Montserrat, Helvetica, sans-serif";
  ctx.fillText("PRICE SHOWN FOR REFERENCE ONLY.", priceCenter, y);
  ctx.textAlign = "left";
  y += 20;

  ctx.fillStyle = COLORS.rule;
  ctx.fillRect(left, y, textWidth, 1);
  y += 22;

  const characterItems = formatCharacterLabels(material.character).slice(0, 3);
  const finishItems = (Array.isArray(material.feature) ? material.feature : [])
    .filter(Boolean)
    .slice(0, 3);
  ctx.fillStyle = COLORS.label;
  ctx.font = "bold 9px Montserrat, Helvetica, sans-serif";
  ctx.fillText("CHARACTER", left, y);
  ctx.fillText("FINISH", left + col, y);
  y += 16;
  ctx.fillStyle = COLORS.body;
  ctx.font = "12px Montserrat, Helvetica, sans-serif";
  const characterRows = characterItems.length ? characterItems : ["--"];
  const finishRows = finishItems.length ? finishItems : ["--"];
  characterRows.forEach((item, i) => {
    ctx.fillText(`• ${String(item).toUpperCase()}`, left, y + i * 16);
  });
  finishRows.forEach((item, i) => {
    ctx.fillText(`• ${String(item).toUpperCase()}`, left + col, y + i * 16);
  });
  y += Math.max(characterRows.length, finishRows.length) * 16 + 16;

  if (y < footerTop - 24) {
    const sections = getCardInfoSections(material, brandData);
    if (y < footerTop - 28 && sections.notes.items.length > 0) {
      ctx.fillStyle = COLORS.label;
      ctx.font = "bold 9px Montserrat, Helvetica, sans-serif";
      ctx.fillText(
        String(sections.notes.title || "SUSTAINABILITY").toUpperCase(),
        left,
        y,
      );
      y += 16;
      ctx.fillStyle = COLORS.body;
      ctx.font = "12px Montserrat, Helvetica, sans-serif";
      sections.notes.items.slice(0, 3).forEach((item) => {
        wrapText(ctx, `• ${item}`, textWidth, 2).forEach((line) => {
          if (y > footerTop - 16) {
            return;
          }
          ctx.fillText(line, left, y);
          y += 16;
        });
      });
      y += 8;
    }

    if (sections.brands.length > 0 && y < footerTop - 28) {
      ctx.fillStyle = COLORS.label;
      ctx.font = "bold 9px Montserrat, Helvetica, sans-serif";
      ctx.fillText("BRAND INFO", left, y);
      y += 16;
      sections.brands.forEach((row) => {
        if (y > footerTop - 28) {
          return;
        }
        ctx.fillStyle = COLORS.value;
        ctx.font = "bold 11px Montserrat, Helvetica, sans-serif";
        const nameLines = wrapText(
          ctx,
          formatBrandMaterialName(row, material.title),
          textWidth,
          1,
        );
        nameLines.forEach((line) => {
          ctx.fillText(line, left, y);
          y += 14;
        });
        const meta = formatBrandMeta(row);
        if (meta && y <= footerTop - 16) {
          ctx.fillStyle = COLORS.label;
          ctx.font = "10px Montserrat, Helvetica, sans-serif";
          ctx.fillText(meta, left, y);
          y += 14;
        }
      });
    }
  }

  ctx.fillStyle = COLORS.footer;
  ctx.fillRect(x, CARD_H - 48, width, 48);
  ctx.fillStyle = COLORS.accent;
  ctx.fillRect(x, CARD_H - 48, width, 1);
  ctx.fillStyle = COLORS.vendor;
  ctx.font = "bold 11px Montserrat, Helvetica, sans-serif";
  ctx.fillText("MATLIB", left, CARD_H - 20);
  ctx.fillStyle = COLORS.label;
  ctx.font = "10px Montserrat, Helvetica, sans-serif";
  ctx.fillText(
    `${cardSiteBase()}/${material.vendor}/${material.slug}`,
    left,
    CARD_H - 8,
  );
}

function drawNamedOverlay(ctx, material) {
  const fade = ctx.createLinearGradient(0, PHOTO_H - 170, 0, PHOTO_H);
  fade.addColorStop(0, "#00000000");
  fade.addColorStop(1, "#000000a8");
  ctx.fillStyle = fade;
  ctx.fillRect(0, PHOTO_H - 170, PHOTO_W, 170);

  const left = 28;
  const textWidth = PHOTO_W - 56;
  ctx.font = "bold 28px Montserrat, Helvetica, sans-serif";
  const titleLines = wrapText(
    ctx,
    String(material.title || material.slug || "").toUpperCase(),
    textWidth,
    2,
  );
  const titleY = PHOTO_H - 24 - (titleLines.length - 1) * 30;

  ctx.fillStyle = COLORS.vendor;
  ctx.font = "bold 13px Montserrat, Helvetica, sans-serif";
  ctx.fillText(String(material.vendor || "").toUpperCase(), left, titleY - 28);

  ctx.fillStyle = COLORS.title;
  ctx.font = "bold 28px Montserrat, Helvetica, sans-serif";
  titleLines.forEach((line, index) => {
    ctx.fillText(line, left, titleY + index * 30);
  });
}

function renderNamedImageCanvas(imageData, material) {
  return new Promise((resolve, reject) => {
    const photo = new Image();

    photo.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = PHOTO_W;
      canvas.height = PHOTO_H;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Named image failed: canvas is not available"));
        return;
      }

      ctx.fillStyle = "#111616";
      ctx.fillRect(0, 0, PHOTO_W, PHOTO_H);
      ctx.drawImage(photo, 0, 0, PHOTO_W, PHOTO_H);
      drawNamedOverlay(ctx, material);
      resolve(canvas);
    };

    photo.onerror = () => {
      reject(new Error("Named image failed: could not load returned image data"));
    };
    photo.src = imageData;
  });
}

function renderCardCanvas(imageData, material, brandData) {
  return new Promise((resolve, reject) => {
    const photo = new Image();

    photo.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = CARD_W;
      canvas.height = CARD_H;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Catalog card failed: canvas is not available"));
        return;
      }

      ctx.fillStyle = "#111616";
      ctx.fillRect(0, 0, CARD_W, CARD_H);
      ctx.drawImage(photo, 0, 0, PHOTO_W, PHOTO_H);
      drawRail(ctx, material, brandData);
      resolve(canvas);
    };

    photo.onerror = () => {
      reject(new Error("Catalog card failed: could not load returned image data"));
    };
    photo.src = imageData;
  });
}

function triggerDownload(canvas, material, audience = "", format = "card") {
  const link = document.createElement("a");
  link.download = cardFileName(material.vendor, material.slug, audience, format);
  link.href = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  link.click();
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Could not create card image"));
        return;
      }
      resolve(blob);
    }, type, quality);
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read card image"));
    reader.readAsDataURL(blob);
  });
}

function jpegToHtmlBlob(blob, width, height) {
  return blobToDataUrl(blob).then(
    (dataUrl) =>
      new Blob(
        [
          `<img src="${dataUrl}" width="${width}" height="${height}" alt="Matlib catalog card" />`,
        ],
        { type: "text/html" },
      ),
  );
}

function buildCardCanvas(imageUrl, material, brandData, format = "card") {
  return fetchImageData(imageUrl).then((imageData) =>
    format === "named"
      ? renderNamedImageCanvas(imageData, material)
      : renderCardCanvas(imageData, material, brandData),
  );
}

export function canCopyCatalogCard() {
  return (
    typeof navigator !== "undefined" &&
    typeof ClipboardItem !== "undefined" &&
    typeof navigator.clipboard?.write === "function"
  );
}

export default function downloadCatalogCard({
  imageUrl,
  material,
  brandData = [],
  brandAudience = "",
  format = "card",
  action = "download",
}) {
  if (!imageUrl || !material?.vendor || !material?.slug) {
    return Promise.resolve(null);
  }

  if (action === "clipboard") {
    const canvasPromise = buildCardCanvas(
      imageUrl,
      material,
      brandData,
      format,
    );
    const jpegPromise = canvasPromise.then((canvas) =>
      canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY),
    );
    const htmlPromise = Promise.all([canvasPromise, jpegPromise]).then(
      ([canvas, blob]) => jpegToHtmlBlob(blob, canvas.width, canvas.height),
    );

    if (!canCopyCatalogCard()) {
      return canvasPromise
        .then((canvas) => {
          triggerDownload(canvas, material, brandAudience, format);
          return "download";
        })
        .catch((error) => {
          console.error("Catalog card copy failed:", error);
          return null;
        });
    }

    const writeClipboard = (items) =>
      navigator.clipboard.write([new ClipboardItem(items)]);

    return writeClipboard({ "text/html": htmlPromise })
      .catch(() => writeClipboard({ "image/jpeg": jpegPromise }))
      .then(() => "clipboard")
      .catch((error) => {
        console.error("Catalog card copy failed:", error);
        return canvasPromise
          .then((canvas) => {
            triggerDownload(canvas, material, brandAudience, format);
            return "download";
          })
          .catch(() => null);
      });
  }

  return fetchImageData(imageUrl)
    .then((imageData) => {
      const render =
        format === "named"
          ? renderNamedImageCanvas(imageData, material)
          : renderCardCanvas(imageData, material, brandData);
      render
        .then((canvas) => {
          triggerDownload(canvas, material, brandAudience, format);
        })
        .catch((error) => {
          console.error("Catalog card download failed:", error);
        });
      return imageData;
    })
    .catch((error) => {
      console.error("Catalog card download failed:", error);
      return null;
    });
}
